import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getMyPersonas,
  deletePersona,
  getMyProfiles,
  deleteProfile,
} from '../services/apiService';
import { useUserStatus } from '../context/UserStatusContext';
import PersonaImage from '../components/molecules/PersonaImage';
import CreateProfileModal from '../components/profile/CreateProfileModal';
import EgoCardHorizontal from '../components/molecules/EgoCardHorizontal';
import { useCachedApi } from '../hooks/useCachedApi';
import styles from './PersonasPage.module.css';
import html2canvas from 'html2canvas';

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
};

function PersonasPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { userStatus } = useUserStatus();
  const { getAccessTokenSilently } = useAuth0();

  const [myProfiles, setMyProfiles] = useState([]);
  const [myCreations, setMyCreations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [activeTab, setActiveTab] = useState('profiles');

  const [showCreationChoice, setShowCreationChoice] = useState(false);
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [cardToShow, setCardToShow] = useState(null);
  const [generatedCardUrl, setGeneratedCardUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const cardGeneratorRef = useRef(null);
  const { data: digitalLegacy } = useCachedApi(
    '/v1/user/digital-legacy',
    15000
  );

  const isPassportReadyForProfile =
    userStatus?.verificationStatus?.hasCompletedBasicTest;
  const atProfileLimit =
    userStatus.maxProfiles !== -1 &&
    myProfiles.length >= userStatus.maxProfiles;
  const atCreationLimit =
    userStatus.maxCreations !== -1 &&
    myCreations.length >= userStatus.maxCreations;

  let profileButtonTitle = 'Create a new public profile';
  if (atProfileLimit) {
    profileButtonTitle = "Your plan's limit has been reached.";
  } else if (!isPassportReadyForProfile) {
    profileButtonTitle =
      'Please complete your personality test in your Passport first.';
  }

  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const accessToken = await getAccessTokenSilently();
      const [creationsData, profilesData] = await Promise.all([
        getMyPersonas(accessToken),
        getMyProfiles(accessToken),
      ]);
      setMyCreations(creationsData || []);
      setMyProfiles(profilesData || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const openDeleteModal = (item) => setItemToDelete(item);
  const closeDeleteModal = () => setItemToDelete(null);

  const handleGenerateCard = () => {
    if (cardGeneratorRef.current) {
      setIsGenerating(true);
      html2canvas(cardGeneratorRef.current, {
        backgroundColor: null,
        useCORS: true,
      }).then((canvas) => {
        setGeneratedCardUrl(canvas.toDataURL('image/png'));
        setIsGenerating(false);
      });
    }
  };

  const closeCardModal = () => {
    setCardToShow(null);
    setGeneratedCardUrl(null);
  };

  const handleCreationSave = () => {
    fetchAllData();
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const accessToken = await getAccessTokenSilently();
      if (itemToDelete.profileId) {
        await deleteProfile(itemToDelete.profileId, accessToken);
      } else {
        await deletePersona(itemToDelete.personaId, accessToken);
      }
      fetchAllData();
    } catch (e) {
      setError(e.message);
    } finally {
      closeDeleteModal();
    }
  };

  const copyUrlToClipboard = (username) => {
    const url = `${window.location.origin}/p/${username}`;
    navigator.clipboard.writeText(url);
  };

  const renderListView = (items, isProfile) => (
    <div className={styles.listContainer}>
      <table className={styles.personaTable}>
        <thead>
          <tr>
            <th>Name</th>
            {/* {isProfile && <th>Engagements (Month)</th>} */}
            {isProfile && <th>Engagements</th>}
            {isProfile && <th>Public URL</th>}
            {!isProfile && <th>Type</th>}
            {!isProfile && <th>Public</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const id = isProfile ? item.profileId : item.personaId;
            const name = isProfile ? item.profileName : item.personaName;
            const type = item.type;
            const isPublic = item.isPublic;
            const editUrl = isProfile
              ? `/profiles/edit/${id}`
              : `/personas/edit/${id}`;
            // The image for a Profile is always inherited from its Digital Legacy.
            const imagePersonaId = isProfile
              ? item.inheritsFrom
              : item.personaId;

            return (
              <tr key={id}>
                <td>
                  <Link
                    to={`/chat/${id}`}
                    state={{ persona: item, isOwner: true }}
                    className={styles.personaNameLink}
                  >
                    <PersonaImage
                      personaId={imagePersonaId}
                      alt={name}
                      className={styles.tableAvatar}
                      isOwner={true}
                    />
                    {name}
                  </Link>
                </td>
                {/* {isProfile && <td className={styles.engagementCell}>{item.engagementThisMonth} 🔥</td>} */}
                {isProfile && (
                  <td className={styles.engagementCell}>
                    {item.engagementTotal} 🔥
                  </td>
                )}
                {!isProfile && (
                  <td>
                    <span className={styles.typeBadge}>{type}</span>
                  </td>
                )}
                {!isProfile && <td>{isPublic ? 'Yes' : 'No'}</td>}
                {isProfile && (
                  <td>
                    <div className={styles.urlCell}>
                      <span
                        className={styles.urlText}
                      >{`/p/${item.username}`}</span>
                      <button
                        onClick={() => copyUrlToClipboard(item.username)}
                        className={styles.copyButton}
                        title="Copy URL"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                  </td>
                )}
                <td>
                  <div className={styles.actionButtons}>
                    {isProfile && (
                      <button
                        onClick={() => setCardToShow(item)}
                        title="Generate Ego Card"
                      >
                        <i className="fas fa-id-card"></i>
                      </button>
                    )}
                    <Link
                      to={editUrl}
                      className={styles.editButton}
                      title="Edit Persona"
                    >
                      <i className="fas fa-pencil-alt"></i>
                    </Link>
                    <button
                      className={styles.deleteButton}
                      onClick={() => openDeleteModal(item)}
                      title="Delete Persona"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderGridView = (items, isProfile) => (
    <div className={styles.personaGrid}>
      {items.map((item) => {
        const id = isProfile ? item.profileId : item.personaId;
        const name = isProfile ? item.profileName : item.personaName;
        const summary = isProfile ? item.overrides?.summary : item.summary;
        const type = isProfile ? 'Profile of Me' : item.type;
        const isPublic = item.isPublic;
        const editUrl = isProfile
          ? `/profiles/edit/${id}`
          : `/personas/edit/${id}`;
        const imagePersonaId = isProfile ? item.inheritsFrom : item.personaId;

        return (
          <div key={id} className={styles.personaCardWrapper}>
            <Link
              to={`/chat/${id}`}
              state={{ persona: item, isOwner: true }}
              className={styles.personaCardLink}
            >
              <div className={styles.personaCard}>
                <PersonaImage
                  personaId={imagePersonaId}
                  alt={name}
                  className={styles.personaCardImage}
                  isOwner={true}
                  cacheKey={item.updatedAt}
                />
                <h3>{name}</h3>
                {/* {isProfile && (<p className={styles.engagementCell}>Engagements (Month): {item.engagementThisMonth} 🔥</p>}) */}
                {isProfile && (
                  <p className={styles.engagementCell}>
                    Engagements: {item.engagementTotal} 🔥
                  </p>
                )}
                {!isProfile && <p className={styles.personaType}>{type}</p>}
                {!isProfile && (
                  <p>{isPublic ? 'Public Persona' : 'Private Persona'}</p>
                )}
                <p className={styles.personaSummary}>{summary}</p>
              </div>
            </Link>

            <div className={styles.actionButtons}>
              {isProfile && (
                <button
                  onClick={() => setCardToShow(item)}
                  title="Generate Ego Card"
                >
                  <i className="fas fa-id-card"></i>
                </button>
              )}
              <Link
                to={editUrl}
                className={styles.editButton}
                title="Edit Persona"
              >
                <i className="fas fa-pencil-alt"></i>
              </Link>
              <button
                className={styles.deleteButton}
                onClick={() => openDeleteModal(item)}
                title="Delete Persona"
              >
                <i className="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderActiveView = (items, emptyMessage, isProfile) => {
    if (!items || items.length === 0) {
      return <p className={styles.emptyStateMessage}>{emptyMessage}</p>;
    }
    if (isMobile) {
      return renderGridView(items, isProfile);
    }
    return viewMode === 'list'
      ? renderListView(items, isProfile)
      : renderGridView(items, isProfile);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>My Public Personas</h2>
          <p className={styles.pageDescription}>
            Manage the AI representatives you share with the world. Create
            context-specific "Profiles of Me" for professional use, or build
            entirely new "Creative Personas" from your imagination.
          </p>
        </div>
        <div className={styles.headerActions}>
          {!isMobile && (
            <div className={styles.viewToggle}>
              <button
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? styles.active : ''}
                title="List View"
              >
                <i className="fas fa-list"></i>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? styles.active : ''}
                title="Grid View"
              >
                <i className="fas fa-th-large"></i>
              </button>
            </div>
          )}
          <button
            onClick={() => setShowCreationChoice(true)}
            className="action-button primary"
            disabled={atProfileLimit && atCreationLimit}
          >
            {atProfileLimit && atCreationLimit
              ? 'Limit Reached'
              : 'Create Persona'}
          </button>
        </div>
      </div>

      <div className={styles.tabNav}>
        <button
          onClick={() => setActiveTab('profiles')}
          className={`${styles.tabButton} ${activeTab === 'profiles' ? styles.active : ''}`}
        >
          <span>Profiles of Me</span>
          <span className={styles.countInfo}>
            <span className={styles.countBadge}>{myProfiles.length}</span>
            <span className={styles.limitText}>
              /{' '}
              {userStatus.maxProfiles === -1
                ? 'Unlimited'
                : userStatus.maxProfiles}
            </span>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('creations')}
          className={`${styles.tabButton} ${activeTab === 'creations' ? styles.active : ''}`}
        >
          <span>Creative Personas</span>
          <span className={styles.countInfo}>
            <span className={styles.countBadge}>{myCreations.length}</span>
            <span className={styles.limitText}>
              /{' '}
              {userStatus.maxCreations === -1
                ? 'Unlimited'
                : userStatus.maxCreations}
            </span>
          </span>
        </button>
      </div>

      {isLoading && <p>Loading personas...</p>}
      {error && <p className="errorText">Error: {error}</p>}

      {!isLoading && !error && (
        <div className={styles.tabContent}>
          {activeTab === 'profiles' &&
            renderActiveView(
              myProfiles,
              "You haven't created any 'Profiles of Me' yet. Create a new profile to have a tailored AI version of yourself for a specific audience, like your LinkedIn or personal website.",
              true
            )}
          {activeTab === 'creations' &&
            renderActiveView(
              myCreations,
              "You haven't created any 'Creative Personas' yet. Build a new persona from scratch to bring a historical figure, fictional character, or brand mascot to life!",
              false
            )}
        </div>
      )}

      {showCreationChoice && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowCreationChoice(false)}
        >
          <div
            className={styles.creationChoiceModal}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>What would you like to create?</h3>
            <p className={styles.modalDescription}>
              Choose a type to get started.
            </p>

            <div className={styles.choiceButtons}>
              <div className={styles.choiceOption}>
                <h4>A Profile of Me</h4>
                <p>
                  Create a version of your Digital Passport for a specific
                  audience (e.g., a professional profile for work).
                </p>
                {!isPassportReadyForProfile && (
                  <p className={styles.requirementText}>
                    <i className="fas fa-lock"></i> Requires a completed
                    personality test.
                  </p>
                )}
                <button
                  onClick={() => {
                    setShowCreationChoice(false);
                    setShowCreateProfileModal(true);
                  }}
                  className="action-button secondary"
                  disabled={atProfileLimit || !isPassportReadyForProfile}
                  title={profileButtonTitle}
                >
                  Create Profile
                </button>
              </div>

              <div className={styles.choiceOption}>
                <h4>A Creative Persona</h4>
                <p>
                  Build a new persona from scratch, like a fictional character,
                  historical figure, or brand mascot.
                </p>
                <button
                  onClick={() => navigate('/create-persona')}
                  className="action-button secondary"
                  disabled={atCreationLimit}
                  title={
                    atCreationLimit ? `Your plan's limit has been reached.` : ''
                  }
                >
                  Create Persona
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateProfileModal && (
        <CreateProfileModal
          onClose={() => setShowCreateProfileModal(false)}
          onSave={handleCreationSave}
        />
      )}

      {itemToDelete && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete "
              <strong>
                {itemToDelete?.profileName || itemToDelete?.personaName}
              </strong>
              "? This action cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button
                onClick={closeDeleteModal}
                className="action-button secondary"
              >
                Cancel
              </button>
              <button onClick={handleDelete} className="action-button primary">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {cardToShow && (
        <div className={styles.modalOverlay} onClick={closeCardModal}>
          <div
            className={styles.cardModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Shareable Ego Card</h3>

            {generatedCardUrl ? (
              <div className={styles.generatedCardContainer}>
                <img
                  src={generatedCardUrl}
                  alt="Generated Ego Card"
                  className={styles.generatedCardImage}
                />
                <div className={styles.cardActions}>
                  <a
                    href={generatedCardUrl}
                    download={`ego-card-${cardToShow.username}.png`}
                    className="action-button primary"
                  >
                    <i className="fas fa-download"></i> Download
                  </a>
                  {/* Add Share on LinkedIn button here */}
                </div>
              </div>
            ) : (
              <div>
                <p>Generate a shareable image for your public profile.</p>
                <button
                  onClick={handleGenerateCard}
                  className="action-button primary"
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate Card'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
        {cardToShow && !generatedCardUrl && (
          <div ref={cardGeneratorRef}>
            <EgoCardHorizontal
              publicProfile={cardToShow}
              digitalLegacy={digitalLegacy}
              userStatus={userStatus}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default withAuthenticationRequired(PersonasPage);
